import merge from 'lodash/merge';
import { useEffect, useMemo } from 'react';
// @mui
import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeProvider as MuiThemeProvider, ThemeOptions } from '@mui/material/styles';
// locales
import { useLocales } from '../locales';
// components
import { useSettingsContext } from '../components/settings';
// system
import { customShadows } from './custom-shadows';
import { componentsOverrides } from './overrides';
import { palette } from './palette';
import { shadows } from './shadows';
import { typography } from './typography';
// options
import { contrast } from './options/contrast';
import { darkMode } from './options/dark-mode';
import { presets } from './options/presets';

// ----------------------------------------------------------------------

type CoreThemeProviderProps = {
  children: React.ReactNode;
  authThemeMode?: 'light' | 'dark';
};

export function CoreThemeProvider({ children, authThemeMode }: CoreThemeProviderProps) {
  const { currentLang } = useLocales();

  const settings = useSettingsContext();
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const forceAuthTheme = pathname.startsWith('/auth') && authThemeMode;
  const themeMode = forceAuthTheme ? authThemeMode : settings.themeMode;

  const darkModeOption = darkMode(themeMode);

  const presetsOption = presets(settings.themeColorPresets);

  const contrastOption = contrast(settings.themeContrast === 'bold', themeMode);

  const baseOption = useMemo(
    () => ({
      palette: palette(themeMode),
      shadows: shadows(themeMode),
      customShadows: customShadows(themeMode),
      typography,
      shape: { borderRadius: 8 },
    }),
    [themeMode]
  );

  const memoizedValue = useMemo(
    () =>
      merge(
        // Base
        baseOption,
        // Dark mode: remove if not in use
        darkModeOption,
        // Presets: remove if not in use
        presetsOption,
        // Contrast: remove if not in use
        contrastOption.theme
      ),
    [baseOption, darkModeOption, presetsOption, contrastOption.theme]
  );

  const theme = createTheme(memoizedValue as ThemeOptions);

  theme.components = merge(componentsOverrides(theme), contrastOption.components);

  const themeWithLocale = useMemo(() => createTheme(theme, currentLang.systemValue), [currentLang.systemValue, theme]);

  useEffect(() => {
    const themeColor = themeWithLocale.palette.background.default;
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');

    document.documentElement.style.colorScheme = themeWithLocale.palette.mode;

    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', themeColor);
    }
  }, [themeWithLocale]);

  return (
    <MuiThemeProvider theme={themeWithLocale}>
      <>
        <CssBaseline />
        {children}
      </>
    </MuiThemeProvider>
  );
}

export default CoreThemeProvider;
